import { CommonModule } from '@angular/common';
import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { SafeHtml } from '@angular/platform-browser';

import {
  AssistantMessage,
  AssistantService,
  AssistantStreamEvent,
  AssistantThread,
  AssistantToolCall,
  AssistantToolResult,
} from '../../services/assistant.service';
import { AuthService } from '../../services/auth.service';
import { MarkdownService } from '../../services/markdown.service';
import {
  AiAgent,
  AiAgentsService,
  PendingConfirmation,
  RightHandStreamEvent,
} from '../../services/ai-agents.service';
import { SupportTicketsService } from '../../services/support-tickets.service';
import { ConfirmService } from '../dialog/confirm.service';
import { ToastService } from '../toast/toast.service';

interface SuggestionChip {
  icon: string;
  label: string;
  prompt: string;
}

/** Mode de fonctionnement de la bulle : assistant générique ou bras droit. */
export type BubbleMode = 'assistant' | 'right_hand';

const STORAGE_OPEN_KEY = 'vd-ai-bubble-open';

/**
 * vd-ai-bubble — FAB global d'assistant IA contextuel.
 *
 * UX :
 *  - FAB rond fixed bottom-right (z-index 9999, dégradé brand).
 *  - Panel glassmorphism (400px desktop, full-screen mobile).
 *  - Liste de messages scrollable avec rendu markdown, tool_calls inline,
 *    tool_results collapsibles.
 *  - Empty state + suggestions chips.
 *  - Sub-drawer "Historique" (liste des threads, archiver/supprimer).
 *  - Hot-stream : tokens concaténés en temps réel sur le dernier message.
 *
 * Auth :
 *  - Recheck le cookie `access_token` à intervalle régulier (3s) + sur
 *    NavigationEnd. Si pas de token, le composant ne rend rien.
 *
 * A11y :
 *  - FAB aria-label + tooltip
 *  - Panel role=dialog + ESC ferme
 *  - Focus auto sur le textarea à l'ouverture
 */
@Component({
  selector: 'vd-ai-bubble',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './ai-bubble.component.html',
  styleUrls: ['./ai-bubble.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VdAiBubbleComponent implements OnInit, OnDestroy, AfterViewChecked {
  private readonly svc = inject(AssistantService);
  private readonly auth = inject(AuthService);
  private readonly md = inject(MarkdownService);
  private readonly confirm = inject(ConfirmService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly aiAgents = inject(AiAgentsService);
  private readonly ticketsApi = inject(SupportTicketsService);

  /**
   * Mode de fonctionnement. `assistant` (défaut) conserve strictement le
   * comportement existant (streaming SSE via AssistantService). `right_hand`
   * route les messages vers le bras droit (POST one-shot) et affiche les
   * actions exécutées sous la réponse.
   */
  @Input() mode: BubbleMode = 'assistant';

  // ── State signals ───────────────────────────────────────────────
  readonly authed = signal<boolean>(false);
  /**
   * Routes plein écran disposant de LEUR PROPRE chat + compositeur fixé en bas
   * (bras droit, marketer). Sur ces pages la bulle globale est redondante ET
   * son FAB chevauche le bouton d'envoi → on la masque entièrement.
   */
  private static readonly HIDDEN_ROUTE_RE = /^\/ai-agents\/(marketer|right-hand|accountant)(\/|$|\?)/;
  readonly hiddenRoute = signal<boolean>(false);
  /** Agent « bras droit » résolu (mode right_hand) pour l'en-tête. */
  readonly rightHandAgent = signal<AiAgent | null>(null);
  readonly open = signal<boolean>(false);
  readonly historyOpen = signal<boolean>(false);
  readonly draftMessage = signal<string>('');
  readonly initialLoad = signal<boolean>(true);
  /** Flag spécifique : skeleton "L'assistant tape..." entre l'envoi user et le 1er token. */
  readonly thinking = signal<boolean>(false);
  /** Tool-call en cours d'exécution (affiche le tag pulsant). */
  readonly runningToolCall = signal<AssistantToolCall | null>(null);
  /** Map d'expansion des tool_results dans le rendu. */
  readonly expandedTools = signal<Record<string, boolean>>({});

  /** Action sensible en attente de confirmation (mode bras droit). */
  readonly pendingConfirm = signal<PendingConfirmation | null>(null);
  /** Confirmation/annulation en cours (désactive les boutons). */
  readonly confirming = signal(false);

  readonly threads = this.svc.threads;
  readonly messages = this.svc.currentMessages;
  readonly currentThreadId = this.svc.currentThreadId;
  readonly config = this.svc.config;
  readonly streaming = this.svc.streaming;
  readonly dailyCount = this.svc.dailyCount;
  readonly errorMsg = this.svc.error;

  readonly hasMessages = computed(() => this.messages().length > 0);
  readonly canSend = computed(
    () => this.draftMessage().trim().length > 0 && !this.streaming() && !this.thinking(),
  );

  readonly modelLabel = computed(() => {
    const cfg = this.config();
    if (!cfg) return 'Assistant IA';
    if (cfg.provider === 'heuristic') return 'Heuristique locale';
    return cfg.model || cfg.provider || 'Assistant IA';
  });

  readonly dailyLeft = computed(() => {
    const cfg = this.config();
    if (!cfg) return null;
    return Math.max(0, cfg.daily_message_cap - this.dailyCount());
  });

  readonly assistantSuggestions: SuggestionChip[] = [
    {
      icon: 'trending_up',
      label: 'Mon CA du mois',
      prompt: "Quel est mon chiffre d'affaires sur le mois en cours ? Détaille par jour.",
    },
    {
      icon: 'group',
      label: 'Top clients',
      prompt: "Liste mes 5 meilleurs clients par chiffre d'affaires des 30 derniers jours.",
    },
    {
      icon: 'schedule',
      label: 'Commandes en retard',
      prompt: 'Quelles commandes sont en attente de traitement depuis plus de 48h ?',
    },
    {
      icon: 'inventory_2',
      label: 'Stock bas',
      prompt: 'Quels produits ont un stock critique (moins de 5 unités) ?',
    },
    {
      icon: 'menu_book',
      label: 'Aide & guide',
      prompt: 'Comment créer et configurer ma boutique en ligne sur vendeur.site ?',
    },
  ];

  /** Suggestions rapides du bras droit (mode right_hand). */
  readonly rightHandSuggestions: SuggestionChip[] = [
    {
      icon: 'wb_sunny',
      label: 'Résume ma journée',
      prompt: 'Résume ma journée : ventes, commandes, messages clients et points d’attention.',
    },
    {
      icon: 'schedule',
      label: 'Qui est en retard ?',
      prompt: 'Quelles commandes ou tâches sont en retard et qui en est responsable ?',
    },
    {
      icon: 'event',
      label: 'Planifie ma semaine',
      prompt: 'Planifie ma semaine en priorisant les tâches importantes et les échéances.',
    },
  ];

  /** Suggestions affichées selon le mode (référence stable par mode). */
  get suggestions(): SuggestionChip[] {
    return this.mode === 'right_hand' ? this.rightHandSuggestions : this.assistantSuggestions;
  }

  // ── Libellés dépendants du mode (assistant inchangé par défaut) ──
  get isRightHand(): boolean {
    return this.mode === 'right_hand';
  }

  get headerTitle(): string {
    if (!this.isRightHand) return 'Assistant IA';
    return this.rightHandAgent()?.name || 'Bras droit';
  }

  get headerIcon(): string {
    return this.isRightHand ? 'handshake' : 'auto_awesome';
  }

  get headerSubtitle(): string {
    if (!this.isRightHand) return this.modelLabel();
    const a = this.rightHandAgent();
    return a?.model_name || a?.specialty || 'Votre bras droit';
  }

  get emptyTitle(): string {
    return this.isRightHand
      ? 'Votre bras droit est à l’écoute'
      : 'Posez-moi une question sur votre boutique';
  }

  get emptyDesc(): string {
    return this.isRightHand
      ? 'Je pilote votre activité, planifie vos tâches et peux agir pour vous.'
      : 'Je peux analyser vos ventes, vos clients, votre stock et bien plus.';
  }

  get typingLabel(): string {
    return this.isRightHand ? 'Le bras droit réfléchit…' : "L'assistant tape…";
  }

  @ViewChild('messagesScroll') private messagesScroll?: ElementRef<HTMLDivElement>;
  @ViewChild('inputArea') private inputArea?: ElementRef<HTMLTextAreaElement>;

  private authTimer: ReturnType<typeof setInterval> | null = null;
  private currentStream: ReturnType<AssistantService['streamChat']> | null = null;
  private shouldScroll = false;
  private currentAssistantBuffer = '';
  private currentToolResults: AssistantToolResult[] = [];
  private currentToolCalls: AssistantToolCall[] = [];

  constructor() {
    // ouvre/ferme depuis localStorage (persistant entre rechargements).
    try {
      const stored = localStorage.getItem(STORAGE_OPEN_KEY);
      if (stored === 'true') this.open.set(true);
    } catch { /* noop */ }

    // Effect : à chaque toggle, on persiste l'état.
    effect(() => {
      const v = this.open();
      try {
        localStorage.setItem(STORAGE_OPEN_KEY, String(v));
      } catch { /* noop */ }
    });
  }

  ngOnInit(): void {
    this.refreshAuth();
    this.updateRouteVisibility();
    this.authTimer = setInterval(() => this.refreshAuth(), 3000);
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => { this.refreshAuth(); this.updateRouteVisibility(); });

    if (this.authed() && this.open()) {
      this.bootstrapData();
    }
    if (this.isRightHand && this.authed()) {
      this.loadRightHandAgent();
    }
  }

  private loadRightHandAgent(): void {
    if (this.rightHandAgent()) return;
    this.aiAgents.getRightHand().subscribe({
      next: (a) => this.rightHandAgent.set(a),
      error: () => { /* bras droit non configuré : on garde le libellé par défaut */ },
    });
  }

  ngOnDestroy(): void {
    if (this.authTimer) clearInterval(this.authTimer);
    this.currentStream?.close();
  }

  ngAfterViewChecked(): void {
    if (this.shouldScroll) {
      this.shouldScroll = false;
      this.scrollToBottom();
    }
  }

  @HostListener('document:keydown.escape')
  onEsc(): void {
    if (this.historyOpen()) {
      this.historyOpen.set(false);
      return;
    }
    if (this.open()) this.open.set(false);
  }

  /** Masque la bulle sur les pages chat plein écran (cf. HIDDEN_ROUTE_RE). */
  private updateRouteVisibility(): void {
    const url = (this.router.url || '').split('#')[0];
    const hide = VdAiBubbleComponent.HIDDEN_ROUTE_RE.test(url);
    if (hide !== this.hiddenRoute()) {
      this.hiddenRoute.set(hide);
      if (hide) {
        this.open.set(false);
        this.historyOpen.set(false);
      }
    }
  }

  // ── Auth lifecycle ──────────────────────────────────────────────
  private refreshAuth(): void {
    const token = this.auth.getTokenFromCookies();
    const wasAuthed = this.authed();
    const isAuthed = !!token;
    if (wasAuthed !== isAuthed) {
      this.authed.set(isAuthed);
      if (!isAuthed) {
        this.open.set(false);
        this.historyOpen.set(false);
      }
    }
  }

  private bootstrapData(): void {
    if (!this.authed()) return;
    // En mode bras droit, on n'utilise pas les threads/config de l'assistant
    // générique : le chat est éphémère et routé vers POST /right-hand/chat.
    if (this.isRightHand) {
      this.initialLoad.set(false);
      this.loadRightHandAgent();
      return;
    }
    if (this.initialLoad()) {
      this.svc.getConfig().subscribe({ error: () => { /* silencieux */ } });
    }
    this.svc.listThreads(false, 50).subscribe({
      next: () => this.initialLoad.set(false),
      error: (err) => {
        this.initialLoad.set(false);
        this.toast.warning(
          err?.customMessage || 'Impossible de charger vos conversations.',
          'Assistant',
        );
      },
    });
  }

  // ── UI handlers ────────────────────────────────────────────────
  toggleOpen(): void {
    const next = !this.open();
    this.open.set(next);
    if (next) {
      this.bootstrapData();
      setTimeout(() => this.inputArea?.nativeElement?.focus(), 200);
    } else {
      this.historyOpen.set(false);
    }
  }

  closePanel(): void {
    this.open.set(false);
    this.historyOpen.set(false);
  }

  openHistory(): void {
    this.historyOpen.set(true);
    this.svc.listThreads(false, 50).subscribe({
      error: (err) =>
        this.toast.warning(
          err?.customMessage || 'Impossible de charger les conversations.',
          'Assistant',
        ),
    });
  }

  closeHistory(): void {
    this.historyOpen.set(false);
  }

  newConversation(): void {
    this.currentStream?.close();
    this.currentStream = null;
    this.svc.resetCurrentThread();
    this.draftMessage.set('');
    this.thinking.set(false);
    this.runningToolCall.set(null);
    setTimeout(() => this.inputArea?.nativeElement?.focus(), 100);
  }

  selectThread(thread: AssistantThread): void {
    this.historyOpen.set(false);
    this.svc.getThread(thread.id).subscribe({
      next: () => {
        this.shouldScroll = true;
      },
      error: (err) =>
        this.toast.danger(
          err?.customMessage || 'Conversation introuvable.',
          'Assistant',
        ),
    });
  }

  async archiveThread(thread: AssistantThread, ev: Event): Promise<void> {
    ev.stopPropagation();
    const ok = await this.confirm.ask({
      title: 'Archiver cette conversation ?',
      message: `« ${thread.title || 'Sans titre'} » sera retirée de votre historique.`,
      tone: 'warning',
      icon: 'archive',
      confirmLabel: 'Archiver',
    });
    if (!ok) return;
    this.svc.archiveThread(thread.id).subscribe({
      next: () => this.toast.success('Conversation archivée.', 'Assistant'),
      error: (err) =>
        this.toast.danger(
          err?.customMessage || "Archivage impossible.",
          'Assistant',
        ),
    });
  }

  async deleteThread(thread: AssistantThread, ev: Event): Promise<void> {
    ev.stopPropagation();
    const ok = await this.confirm.ask({
      title: 'Supprimer cette conversation ?',
      message: 'Suppression définitive — les messages seront perdus.',
      tone: 'danger',
      icon: 'delete_forever',
      confirmLabel: 'Supprimer',
    });
    if (!ok) return;
    this.svc.deleteThread(thread.id).subscribe({
      next: () => this.toast.success('Conversation supprimée.', 'Assistant'),
      error: (err) =>
        this.toast.danger(
          err?.customMessage || 'Suppression impossible.',
          'Assistant',
        ),
    });
  }

  onPromptSuggestion(chip: SuggestionChip): void {
    this.draftMessage.set(chip.prompt);
    setTimeout(() => this.send(), 0);
  }

  onInputChange(value: string): void {
    this.draftMessage.set(value);
  }

  onInputKeydown(ev: KeyboardEvent): void {
    // Enter envoie, Shift+Enter saute une ligne.
    if (ev.key === 'Enter' && !ev.shiftKey) {
      ev.preventDefault();
      this.send();
    }
  }

  retry(): void {
    const last = [...this.messages()].reverse().find((m) => m.role === 'user');
    if (!last) return;
    this.svc.error.set(null);
    this.draftMessage.set(last.content?.text || '');
    this.send();
  }

  // ── Envoi + streaming SSE ───────────────────────────────────────
  send(): void {
    const text = this.draftMessage().trim();
    if (!text || this.streaming() || this.thinking()) return;
    if (!this.authed()) return;

    // Commandes inline /ticket : traitées en local (helpdesk natif),
    // SANS appel au LLM ni consommation du quota journalier.
    if (/^\/ticket(\s|$)/i.test(text)) {
      this.draftMessage.set('');
      this.svc.error.set(null);
      this.svc.appendMessageLocally({
        id: `temp-user-${Date.now()}`,
        thread_id: this.currentThreadId() || '',
        role: 'user',
        content: { text },
        created_at: new Date().toISOString(),
      });
      this.shouldScroll = true;
      this.handleTicketCommand(text);
      return;
    }

    this.draftMessage.set('');
    this.svc.error.set(null);

    // Bulle user immédiate (id temporaire — sera remplacé in-place par le done event).
    const userMsg: AssistantMessage = {
      id: `temp-user-${Date.now()}`,
      thread_id: this.currentThreadId() || '',
      role: 'user',
      content: { text },
      created_at: new Date().toISOString(),
    };
    this.svc.appendMessageLocally(userMsg);

    // Bulle assistant vide en cours de stream.
    const assistantMsg: AssistantMessage = {
      id: `temp-asst-${Date.now()}`,
      thread_id: this.currentThreadId() || '',
      role: 'assistant',
      content: { text: '' },
      tool_calls: [],
      tool_results: [],
      created_at: new Date().toISOString(),
    };
    this.svc.appendMessageLocally(assistantMsg);

    this.thinking.set(true);
    this.shouldScroll = true;
    this.currentAssistantBuffer = '';
    this.currentToolCalls = [];
    this.currentToolResults = [];

    // Mode bras droit : réponse one-shot + actions exécutées (pas de SSE).
    if (this.isRightHand) {
      this.sendRightHand(text);
      return;
    }

    this.currentStream = this.svc.streamChat(this.currentThreadId(), text);
    this.currentStream.events$.subscribe({
      next: (ev: AssistantStreamEvent) => this.handleStreamEvent(ev),
      error: (err: unknown) => this.handleStreamError(err),
      complete: () => {
        this.thinking.set(false);
        this.runningToolCall.set(null);
      },
    });
    this.svc.bumpDailyCount();
  }

  /**
   * Envoi en mode bras droit EN STREAMING : SSE via fetch. Chaque `delta` est
   * accumulé et posé en direct sur la dernière bulle assistant ; le `done`
   * event finalise et expose une éventuelle action à confirmer.
   */
  private sendRightHand(text: string): void {
    this.pendingConfirm.set(null);
    let streamed = false;
    this.aiAgents.rightHandChatStream(text, 'bubble').subscribe({
      next: (ev: RightHandStreamEvent) => {
        if (ev.delta) {
          streamed = true;
          this.thinking.set(false);
          this.currentAssistantBuffer += ev.delta;
          this.svc.replaceLastAssistant({
            id: `temp-asst-stream`,
            thread_id: this.currentThreadId() || '',
            role: 'assistant',
            content: { text: this.currentAssistantBuffer },
            created_at: new Date().toISOString(),
          });
          this.shouldScroll = true;
        }
        if (ev.done) {
          this.thinking.set(false);
          const finalText = ev.reply || this.currentAssistantBuffer || '';
          this.svc.replaceLastAssistant({
            id: `rh-${Date.now()}`,
            thread_id: this.currentThreadId() || '',
            role: 'assistant',
            content: { text: finalText },
            created_at: new Date().toISOString(),
          });
          if (ev.pending_confirmation?.token) {
            this.pendingConfirm.set(ev.pending_confirmation);
          }
          this.shouldScroll = true;
        }
      },
      error: (err: unknown) => {
        // Fallback non-stream si le streaming échoue (réseau/SSE).
        if (!streamed) {
          this.sendRightHandFallback(text);
          return;
        }
        this.thinking.set(false);
        const detail = this.extractError(err);
        this.svc.error.set(detail);
      },
      complete: () => {
        this.thinking.set(false);
      },
    });
  }

  /** Repli non-stream si le streaming SSE n'aboutit pas. */
  private sendRightHandFallback(text: string): void {
    this.aiAgents.rightHandChat(text, 'bubble').subscribe({
      next: (res) => {
        this.thinking.set(false);
        if (res.thread_id && !this.currentThreadId()) {
          this.svc.currentThreadId.set(res.thread_id);
        }
        const toolResults = this.mapActionsToToolResults(res.actions_executed);
        this.svc.replaceLastAssistant({
          id: `rh-${Date.now()}`,
          thread_id: this.currentThreadId() || '',
          role: 'assistant',
          content: { text: res.reply || '' },
          tool_calls: null,
          tool_results: toolResults.length ? toolResults : null,
          latency_ms: res.run?.latency_ms ?? null,
          created_at: new Date().toISOString(),
        });
        if (res.pending_confirmation?.token) {
          this.pendingConfirm.set(res.pending_confirmation);
        }
        this.shouldScroll = true;
      },
      error: (err: unknown) => {
        this.thinking.set(false);
        const detail = this.extractError(err);
        this.svc.error.set(detail);
        this.svc.replaceLastAssistant({
          id: `rh-error-${Date.now()}`,
          thread_id: this.currentThreadId() || '',
          role: 'assistant',
          content: { text: '' },
          error: detail,
          created_at: new Date().toISOString(),
        });
        this.shouldScroll = true;
      },
    });
  }

  private extractError(err: unknown): string {
    return (
      (err as { error?: { detail?: string }; customMessage?: string } | null)?.error?.detail ||
      (err as { customMessage?: string } | null)?.customMessage ||
      'Le bras droit n’a pas pu répondre.'
    );
  }

  /** Confirme l'action sensible en attente (mode bras droit). */
  confirmPending(): void {
    const pc = this.pendingConfirm();
    if (!pc?.token || this.confirming()) return;
    this.confirming.set(true);
    this.aiAgents.rightHandConfirm(pc.token).subscribe({
      next: (res) => {
        this.confirming.set(false);
        this.pendingConfirm.set(null);
        this.svc.appendMessageLocally({
          id: `rh-confirm-${Date.now()}`,
          thread_id: this.currentThreadId() || '',
          role: 'assistant',
          content: { text: res.reply || '✅ Action effectuée.' },
          created_at: new Date().toISOString(),
        });
        this.shouldScroll = true;
      },
      error: () => {
        this.confirming.set(false);
        this.pendingConfirm.set(null);
        this.toast.danger('Action impossible (expirée ou refusée).');
      },
    });
  }

  /** Annule l'action sensible en attente (mode bras droit). */
  cancelPending(): void {
    const pc = this.pendingConfirm();
    if (!pc?.token || this.confirming()) return;
    this.confirming.set(true);
    this.aiAgents.rightHandCancel(pc.token).subscribe({
      next: (res) => {
        this.confirming.set(false);
        this.pendingConfirm.set(null);
        this.svc.appendMessageLocally({
          id: `rh-cancel-${Date.now()}`,
          thread_id: this.currentThreadId() || '',
          role: 'assistant',
          content: { text: res.reply || 'Action annulée.' },
          created_at: new Date().toISOString(),
        });
        this.shouldScroll = true;
      },
      error: () => {
        this.confirming.set(false);
        this.pendingConfirm.set(null);
      },
    });
  }

  /** Normalise les actions exécutées par le bras droit en tool_results. */
  private mapActionsToToolResults(actions: unknown[] | null | undefined): AssistantToolResult[] {
    if (!Array.isArray(actions)) return [];
    return actions.map((raw) => {
      const a = (raw ?? {}) as Record<string, unknown>;
      const name = a['tool'] || a['name'] || a['type'] || a['action'] || 'action';
      const output = a['output'] ?? a['result'] ?? a['detail'] ?? raw;
      return { name: String(name), output };
    });
  }

  private handleStreamEvent(ev: AssistantStreamEvent): void {
    this.shouldScroll = true;
    switch (ev.type) {
      case 'meta': {
        const newThreadId = ev.data?.thread_id as string | undefined;
        if (newThreadId && !this.currentThreadId()) {
          this.svc.currentThreadId.set(newThreadId);
        }
        return;
      }
      case 'token': {
        const chunk = String(ev.data?.text || '');
        this.thinking.set(false);
        this.currentAssistantBuffer += chunk;
        this.svc.replaceLastAssistant({
          id: `temp-asst-stream`,
          thread_id: this.currentThreadId() || '',
          role: 'assistant',
          content: { text: this.currentAssistantBuffer },
          tool_calls: this.currentToolCalls.slice(),
          tool_results: this.currentToolResults.slice(),
          created_at: new Date().toISOString(),
        });
        return;
      }
      case 'tool_call': {
        const tc: AssistantToolCall = {
          id: ev.data?.id,
          name: String(ev.data?.name || 'tool'),
          input: ev.data?.input,
        };
        this.currentToolCalls.push(tc);
        this.runningToolCall.set(tc);
        this.svc.replaceLastAssistant({
          id: `temp-asst-stream`,
          thread_id: this.currentThreadId() || '',
          role: 'assistant',
          content: { text: this.currentAssistantBuffer },
          tool_calls: this.currentToolCalls.slice(),
          tool_results: this.currentToolResults.slice(),
          created_at: new Date().toISOString(),
        });
        return;
      }
      case 'tool_result': {
        const tr: AssistantToolResult = {
          name: String(ev.data?.name || 'tool'),
          output: ev.data?.output,
        };
        this.currentToolResults.push(tr);
        this.runningToolCall.set(null);
        this.svc.replaceLastAssistant({
          id: `temp-asst-stream`,
          thread_id: this.currentThreadId() || '',
          role: 'assistant',
          content: { text: this.currentAssistantBuffer },
          tool_calls: this.currentToolCalls.slice(),
          tool_results: this.currentToolResults.slice(),
          created_at: new Date().toISOString(),
        });
        return;
      }
      case 'done': {
        this.thinking.set(false);
        this.runningToolCall.set(null);
        // Met à jour le message final avec l'id réel + latence.
        const messageId = String(ev.data?.message_id || `temp-asst-final`);
        this.svc.replaceLastAssistant({
          id: messageId,
          thread_id: String(ev.data?.thread_id || this.currentThreadId() || ''),
          role: 'assistant',
          content: { text: this.currentAssistantBuffer },
          tool_calls: this.currentToolCalls.length ? this.currentToolCalls.slice() : null,
          tool_results: this.currentToolResults.length ? this.currentToolResults.slice() : null,
          latency_ms: ev.data?.latency_ms ?? null,
          created_at: new Date().toISOString(),
        });
        // Rafraîchit la liste threads pour reclasser le thread courant en haut.
        this.svc.listThreads(false, 50).subscribe({ error: () => {} });
        return;
      }
      case 'error': {
        const code = String(ev.data?.error || '');
        let detail: string;
        if (code === 'insufficient_credits') {
          const balance = ev.data?.balance;
          detail =
            'Crédits IA insuffisants. Rechargez vos crédits pour continuer.' +
            (typeof balance === 'number' ? ` (solde : ${balance})` : '');
        } else {
          detail = String(ev.data?.detail || ev.data?.error || 'Erreur inconnue');
        }
        this.svc.error.set(String(detail));
        this.thinking.set(false);
        this.runningToolCall.set(null);
        // Marque le message assistant en erreur.
        this.svc.replaceLastAssistant({
          id: `temp-asst-error`,
          thread_id: this.currentThreadId() || '',
          role: 'assistant',
          content: { text: this.currentAssistantBuffer || '' },
          error: String(detail),
          created_at: new Date().toISOString(),
        });
        return;
      }
    }
  }

  private handleStreamError(err: unknown): void {
    const msg = (err as { message?: string } | null | undefined)?.message
      ?? (typeof err === 'string' ? err : 'Connexion perdue');
    this.svc.error.set(String(msg));
    this.thinking.set(false);
    this.runningToolCall.set(null);
  }

  // ── Commandes inline /ticket (helpdesk natif, sans LLM) ─────────
  /**
   * Parse et exécute `/ticket create|list|status|reply|help`. Les réponses
   * sont injectées comme messages assistant locaux (même structure que le
   * fil normal) — aucun stream SSE, aucun bump du quota journalier.
   */
  private handleTicketCommand(raw: string): void {
    const rest = raw.replace(/^\/ticket\s*/i, '').trim();

    if (!rest || /^help$/i.test(rest)) {
      this.pushTicketReply(this.ticketHelpText());
      return;
    }

    const createMatch = /^create\s+([\s\S]+)$/i.exec(rest);
    if (createMatch) {
      const subject = createMatch[1].trim();
      this.thinking.set(true);
      this.ticketsApi
        .createTicket({ scope: 'platform_support', subject, source: 'manual' })
        .subscribe({
          next: (r) => this.pushTicketReply(
            `✅ Ticket **#${r.ticket.id}** créé — ${r.ticket.subject}\n\n`
            + `Suivez-le dans le [Centre de support](${this.supportUrl()}).`,
          ),
          error: (err) => this.ticketApiError('Création du ticket impossible', err),
        });
      return;
    }
    if (/^create$/i.test(rest)) {
      this.pushTicketReply(
        this.ticketHelpText('⚠️ Précisez un sujet : `/ticket create <sujet>`'),
      );
      return;
    }

    if (/^list$/i.test(rest)) {
      this.thinking.set(true);
      this.ticketsApi.listTickets({ limit: 5 }).subscribe({
        next: (r) => {
          if (!r.items.length) {
            this.pushTicketReply(
              'Aucun ticket pour le moment. Créez-en un avec `/ticket create <sujet>`.',
            );
            return;
          }
          const lines = r.items.map((t) =>
            `- **#${t.id}** · ${this.ticketStatusLabel(t.status)} · ${t.subject}`
            + ` _(priorité ${this.ticketPriorityLabel(t.priority)})_`,
          );
          this.pushTicketReply([
            `📋 **Vos ${r.items.length} derniers tickets** (${r.total} au total) :`,
            '',
            ...lines,
            '',
            `Détail : \`/ticket status <id>\` — ou le [Centre de support](${this.supportUrl()}).`,
          ].join('\n'));
        },
        error: (err) => this.ticketApiError('Liste des tickets indisponible', err),
      });
      return;
    }

    const statusMatch = /^status\s+#?(\d+)\s*$/i.exec(rest);
    if (statusMatch) {
      const id = Number(statusMatch[1]);
      this.thinking.set(true);
      this.ticketsApi.getTicket(id).subscribe({
        next: (r) => {
          const t = r.ticket;
          const lines = [
            `🎫 **Ticket #${t.id}** — ${t.subject}`,
            `Statut : **${this.ticketStatusLabel(t.status)}** · `
            + `Priorité : ${this.ticketPriorityLabel(t.priority)}`,
          ];
          const msgs = r.messages || [];
          const last = [...msgs].reverse().find((m) => !m.internal) || msgs[msgs.length - 1];
          if (last) {
            const excerpt = last.body.length > 240 ? `${last.body.slice(0, 240)}…` : last.body;
            lines.push('', `Dernier message (${last.author_role}) :`, `> ${excerpt.replace(/\n/g, ' ')}`);
          } else {
            lines.push('', '_Aucun message dans ce ticket._');
          }
          lines.push('', `Répondre : \`/ticket reply ${t.id} <message>\``);
          this.pushTicketReply(lines.join('\n'));
        },
        error: (err) => this.ticketApiError(`Ticket #${id} introuvable`, err),
      });
      return;
    }

    const replyMatch = /^reply\s+#?(\d+)\s+([\s\S]+)$/i.exec(rest);
    if (replyMatch) {
      const id = Number(replyMatch[1]);
      const body = replyMatch[2].trim();
      this.thinking.set(true);
      this.ticketsApi.postMessage(id, body).subscribe({
        next: () => this.pushTicketReply(`✅ Réponse envoyée sur le ticket **#${id}**.`),
        error: (err) => this.ticketApiError(`Réponse au ticket #${id} impossible`, err),
      });
      return;
    }

    this.pushTicketReply(this.ticketHelpText('⚠️ Commande non reconnue.'));
  }

  /** Injecte une réponse assistant locale dans le fil (sans LLM). */
  private pushTicketReply(text: string): void {
    this.thinking.set(false);
    this.svc.appendMessageLocally({
      id: `ticket-cmd-${Date.now()}`,
      thread_id: this.currentThreadId() || '',
      role: 'assistant',
      content: { text },
      created_at: new Date().toISOString(),
    });
    this.shouldScroll = true;
  }

  private ticketApiError(prefix: string, err: unknown): void {
    const detail =
      (err as { error?: { detail?: string } } | null)?.error?.detail ||
      (err as { customMessage?: string } | null)?.customMessage ||
      'erreur inconnue';
    this.pushTicketReply(
      `⚠️ ${prefix} : ${detail}\n\n`
      + `Réessayez dans un instant ou passez par le [Centre de support](${this.supportUrl()}).`,
    );
  }

  private supportUrl(): string {
    try {
      return `${window.location.origin}/support`;
    } catch {
      return '/support';
    }
  }

  private ticketHelpText(intro?: string): string {
    return [
      intro || 'ℹ️ **Commandes /ticket disponibles :**',
      '',
      '- `/ticket create <sujet>` — créer un ticket support',
      '- `/ticket list` — vos 5 derniers tickets',
      '- `/ticket status <id>` — statut + dernier message',
      '- `/ticket reply <id> <message>` — répondre à un ticket',
      '- `/ticket help` — cette aide',
      '',
      `Gérez tout dans le [Centre de support](${this.supportUrl()}).`,
    ].join('\n');
  }

  private ticketStatusLabel(s: string): string {
    return ({
      open: 'ouvert',
      in_progress: 'en cours',
      waiting_customer: 'en attente de votre réponse',
      resolved: 'résolu',
      closed: 'fermé',
    } as Record<string, string>)[s] || s;
  }

  private ticketPriorityLabel(p: string): string {
    return ({
      low: 'basse',
      medium: 'moyenne',
      high: 'haute',
      urgent: 'urgente',
    } as Record<string, string>)[p] || p;
  }

  // ── Rendu ──────────────────────────────────────────────────────
  renderMd(text?: string | null): SafeHtml {
    return this.md.renderToSafeHtml(text || '');
  }

  /**
   * Intercepte les clics sur les liens INTERNES rendus dans les messages
   * (ex. citations /docs de la bulle IA) pour naviguer via le routeur Angular
   * sans recharger l'app. Les liens externes (target=_blank) ne sont pas
   * affectés. Ferme le panneau pour révéler la page de destination.
   */
  onBodyClick(ev: MouseEvent): void {
    const anchor = (ev.target as HTMLElement | null)?.closest?.(
      'a[data-internal="1"]',
    ) as HTMLAnchorElement | null;
    if (!anchor) return;
    const href = anchor.getAttribute('href') || '';
    if (!href.startsWith('/') || href.startsWith('//')) return;
    ev.preventDefault();
    const [path, fragment] = href.split('#');
    this.open.set(false);
    this.router.navigateByUrl(path + (fragment ? `#${fragment}` : ''));
  }

  toolResultPreview(tr: AssistantToolResult): string {
    const out = tr?.output;
    if (out == null) return '—';
    try {
      const s = typeof out === 'string' ? out : JSON.stringify(out, null, 2);
      return s.length > 4000 ? s.slice(0, 4000) + '\n…' : s;
    } catch {
      return String(out);
    }
  }

  toolInputPreview(tc: AssistantToolCall): string {
    try {
      const s = typeof tc?.input === 'string' ? tc.input : JSON.stringify(tc?.input ?? {});
      return s.length > 80 ? s.slice(0, 80) + '…' : s;
    } catch {
      return '';
    }
  }

  toggleToolResult(key: string): void {
    this.expandedTools.update((m) => ({ ...m, [key]: !m[key] }));
  }

  isToolResultExpanded(key: string): boolean {
    return !!this.expandedTools()[key];
  }

  trackByMessage = (_: number, m: AssistantMessage) => m.id;
  trackByThread = (_: number, t: AssistantThread) => t.id;
  trackByChip = (_: number, c: SuggestionChip) => c.label;
  trackByToolCall = (_: number, tc: AssistantToolCall) => `${tc.id || tc.name}-${_}`;
  trackByToolResult = (_: number, tr: AssistantToolResult) => `${tr.name}-${_}`;

  relativeTime(iso?: string | null): string {
    if (!iso) return '';
    const d = new Date(iso).getTime();
    if (Number.isNaN(d)) return '';
    const diff = Date.now() - d;
    const min = Math.round(diff / 60000);
    if (min < 1) return "à l'instant";
    if (min < 60) return `il y a ${min} min`;
    const h = Math.round(min / 60);
    if (h < 24) return `il y a ${h} h`;
    const j = Math.round(h / 24);
    if (j < 7) return `il y a ${j} j`;
    return new Date(iso).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
    });
  }

  private scrollToBottom(): void {
    const el = this.messagesScroll?.nativeElement;
    if (!el) return;
    try {
      el.scrollTop = el.scrollHeight;
    } catch { /* noop */ }
  }
}
